import { useEffect, useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ChevronsLeft, Star, Plus, Building2, ChevronsUpDown,
  Search, MoreVertical,
} from 'lucide-react';
import {
  RiHome, RiWallet, RiBox, RiCart, RiBarChart, RiBriefcase,
  RiTeam, RiTools, RiBuilding, RiClipboard, RiSettings,
} from '@/components/icons/remix';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { useUI } from '@/store/ui';
import { useMyPermissions } from '@/lib/permissions';
import { useStarredMenu } from '@/lib/starredMenu';
import { doctypes } from '@/lib/doctypes';
import { me, logout } from '@/lib/auth';
import { Tooltip } from '@/components/Tooltip';
import { Avatar } from '@/components/Avatar';
import { Kbd } from '@/components/Kbd';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/DropdownMenu';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  shortcut?: string;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

// requireAny: the user needs read access to AT LEAST ONE of these doctypes
// for the menu item to show. Items without `requireAny` are always visible
// (e.g. Home). Module umbrella items (e.g. /accounting) list every doctype
// that lives under that module — a Manager who can only read `customer`
// still sees /accounting because it has something inside.
type GuardedNavItem = NavItem & { requireAny?: string[] };

const sections: { label?: string; items: GuardedNavItem[] }[] = [
  {
    items: [
      { to: '/', label: 'Home', icon: RiHome, shortcut: 'G H' },
    ],
  },
  {
    label: 'Modules',
    items: [
      { to: '/accounting',     label: 'Finance',            icon: RiWallet,     requireAny: ['journal_entry','payment_entry','account','tax_template','bank'] },
      { to: '/stock',          label: 'Inventory',          icon: RiBox,    requireAny: ['warehouse','item','stock_entry'] },
      { to: '/buying',         label: 'Procurement',        icon: RiCart,       requireAny: ['material_request','purchase_order','purchase_invoice','supplier'] },
      { to: '/selling',        label: 'Sales',              icon: RiBarChart,   requireAny: ['sales_order','sales_invoice','delivery_note','customer'] },
      { to: '/projects',       label: 'Operations',         icon: RiBriefcase,  requireAny: ['project'] },
      { to: '/hr',             label: 'HR & Payroll',       icon: RiTeam,      requireAny: ['employee'] },
      { to: '/maintenance',    label: 'Asset Operations',   icon: RiTools,     requireAny: ['maintenance_request','asset_work_order'] },
      { to: '/manufacturing',  label: 'Manufacturing',      icon: RiBuilding,  requireAny: ['bom','work_order'] },
      { to: '/assets',         label: 'Assets',             icon: RiClipboard,  requireAny: ['asset'] },
    ],
  },
];

export function Sidebar() {
  const sidebarCollapsed = useUI((s) => s.sidebarCollapsed);
  return (
    <aside
      className={cn(
        // sticky + self-start pins the sidebar to the viewport so it stays
        // full-height while only the page content scrolls — otherwise the
        // h-screen aside scrolls away with the document on tall pages.
        'shrink-0 sticky top-0 self-start h-screen flex flex-col bg-canvas border-r border-hairline transition-[width] duration-150 ease-gentle',
        sidebarCollapsed ? 'w-[68px]' : 'w-[252px]',
      )}
    >
      <SidebarContent collapsed={sidebarCollapsed} showCollapseToggle />
    </aside>
  );
}

/**
 * Inner nav content — workspace switcher + nav sections + footer. Rendered
 * both inside the desktop <aside> and inside the mobile MobileDrawer. The
 * drawer always passes `collapsed=false` and omits the collapse toggle.
 */
export function SidebarContent({
  collapsed,
  showCollapseToggle,
}: {
  collapsed: boolean;
  showCollapseToggle?: boolean;
}) {
  const toggleSidebar = useUI((s) => s.toggleSidebar);
  const router = useRouterState();
  const path = router.location.pathname;
  const { t: _t } = useTranslation();
  const perms = useMyPermissions();
  const { items: stars } = useStarredMenu();

  // Sidebar module highlight. Several doctypes live under a URL namespace that
  // differs from the module they belong to (e.g. /accounting/material-requests
  // belongs to Procurement). Highlight the module the current doctype actually
  // belongs to, falling back to URL-prefix matching for module landings and
  // non-doctype routes.
  const MODULE_LABEL_TO_PATH: Record<string, string> = {
    'Finance': '/accounting', 'Inventory': '/stock', 'Procurement': '/buying',
    'Sales': '/selling', 'Operations': '/projects', 'HR & Payroll': '/hr',
    'Asset Operations': '/maintenance', 'Assets': '/assets',
    'Manufacturing': '/manufacturing',
  };
  const MODULE_PATHS = new Set(Object.values(MODULE_LABEL_TO_PATH));
  const moduleOverridePath = (() => {
    // Operations Overview lives under the Assets module.
    if (path === '/operations/overview' || path.startsWith('/operations/overview/')) return '/assets';
    let best: { len: number; to: string } | null = null;
    for (const d of Object.values(doctypes)) {
      const base = `${d.modulePath}/${d.slug}`;
      if (path === base || path.startsWith(base + '/')) {
        const to = MODULE_LABEL_TO_PATH[d.module];
        if (to && (!best || base.length > best.len)) best = { len: base.length, to };
      }
    }
    return best?.to ?? null;
  })();
  const isActive = (to: string) => {
    if (to === '/') return path === '/';
    if (moduleOverridePath) {
      if (MODULE_PATHS.has(to)) return to === moduleOverridePath;
      return path === to || path.startsWith(to + '/');
    }
    return path === to || path.startsWith(to + '/');
  };

  // Filter each section's items by the caller's read perms. Drop sections
  // that end up empty — a label with zero items is just noise.
  const visibleSections = sections
    .map((s) => ({
      ...s,
      items: s.items.filter((it) => !it.requireAny || perms.canRead_any(it.requireAny)),
    }))
    .filter((s) => s.items.length > 0);

  // Build the dynamic Starred section from the user's saved stars. Icon
  // resolution: prefer the matching DoctypeConfig.icon (so the sidebar
  // looks like the page header); fall back to a generic Star.
  const starredSection: { label: string; items: NavItem[] } | null = stars.length === 0 ? null : {
    label: 'Starred',
    items: stars.map((s) => ({
      to: s.path,
      label: s.label,
      icon: iconForPath(s.path),
    })),
  };

  return (
    <>
      {/* App brand — logogram when expanded (the chunky leaf-L mark reads
       *  cleanly at 28px; the full wordmark's "erp" subscript would be
       *  unreadable at this height). Collapsed sidebar shows the workspace
       *  switcher's square mark below; no logogram there. */}
      {/* Brand header — fixed 56px row with a hairline underline so it lines
       *  up with the 56px top chrome. Full wordmark when expanded, compact
       *  leaf-L mark when the rail is collapsed. Both are the brand SVGs. */}
      <div
        className={cn(
          'h-14 shrink-0 flex items-center border-b border-hairline-soft',
          collapsed ? 'justify-center px-0' : 'px-4',
        )}
      >
        <img
          src={collapsed ? '/logica-erp-mark.svg' : '/logica-erp-logo.svg'}
          alt="Logica ERP"
          className={cn('w-auto select-none', collapsed ? 'h-8' : 'h-9')}
          draggable={false}
        />
      </div>

      {/* Workspace switcher + in-rail search. Search moved OUT of the top
       *  chrome and INTO the rail (Light Pro shell): the topbar now carries
       *  page context, the rail carries navigation + find. Both collapse to
       *  icon-only affordances on the 64px rail. */}
      {!collapsed ? (
        <div className="px-3 pt-3 pb-2 space-y-2">
          <WorkspaceSwitcher />
          <SearchTrigger />
        </div>
      ) : (
        <div className="pt-3 pb-2 flex justify-center">
          <SearchTrigger collapsed />
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-6">
        {starredSection && (
          <div>
            {!collapsed && (
              <div className="px-3 pt-1 pb-2 flex items-center gap-2 text-stone text-micro-uppercase">
                <Star className="size-3" />
                {starredSection.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {starredSection.items.map((item) => {
                const active = isActive(item.to);
                const link = (
                  <Link
                    to={item.to as never}
                    className={cn(
                      'relative flex items-center gap-3 px-3 h-11 md:h-10 rounded-lg text-[14px] transition-colors',
                      active
                        ? 'bg-surface text-ink font-medium'
                        : 'text-steel hover:bg-surface hover:text-ink',
                      collapsed && 'justify-center px-0',
                    )}
                  >
                    <item.icon className={cn('size-5 shrink-0', active && 'text-brand-green-deep')} />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </Link>
                );
                return (
                  <li key={item.to} className="group relative">
                    {collapsed ? <Tooltip side="right" content={item.label}>{link}</Tooltip> : link}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {visibleSections.map((section, sIdx) => (
          <div key={sIdx}>
            {section.label && !collapsed && (
              <div className="px-3 pt-1 pb-2 flex items-center gap-2 text-stone text-micro-uppercase">
                {section.label === 'Starred' && <Star className="size-3" />}
                {section.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = isActive(item.to);
                const link = (
                  <Link
                    to={item.to}
                    className={cn(
                      'relative flex items-center gap-3 px-3 h-11 md:h-10 rounded-lg text-[14px] transition-colors',
                      active
                        ? 'bg-surface text-ink font-medium'
                        : 'text-steel hover:bg-surface hover:text-ink',
                      collapsed && 'justify-center px-0',
                    )}
                  >
                    <item.icon className={cn('size-5 shrink-0', active && 'text-brand-green-deep')} />
                    {!collapsed && (
                      <>
                        <span className="truncate">{item.label}</span>
                        {item.shortcut && (
                          <span className="ml-auto kbd opacity-0 group-hover:opacity-100 transition-opacity">{item.shortcut}</span>
                        )}
                      </>
                    )}
                  </Link>
                );
                return (
                  <li key={item.to} className="group relative">
                    {collapsed ? <Tooltip side="right" content={item.label}>{link}</Tooltip> : link}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer — Settings + Collapse, then the account card that anchors the
       *  rail (fills the dead space the old sparse footer left and gives the
       *  signed-in user a real home for identity + log out). */}
      <div className="px-2 py-2 border-t border-hairline space-y-2">
        <div className="space-y-0.5">
          {[
            { to: '/settings',   label: 'Settings',   icon: RiSettings },
          ].map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className={cn(
                'relative flex items-center gap-3 px-3 h-11 md:h-10 rounded-lg text-[14px] text-steel hover:bg-surface hover:text-ink transition-colors',
                collapsed && 'justify-center px-0',
              )}
            >
              <it.icon className="size-5 shrink-0" />
              {!collapsed && <span className="truncate">{it.label}</span>}
            </Link>
          ))}
          {showCollapseToggle && (
            <button
              type="button"
              onClick={toggleSidebar}
              className={cn(
                'relative flex items-center gap-3 px-3 h-11 md:h-10 w-full rounded-lg text-[14px] text-stone hover:bg-surface hover:text-ink transition-colors',
                collapsed && 'justify-center px-0',
              )}
              aria-label="Toggle sidebar"
            >
              <ChevronsLeft className={cn('size-5 shrink-0 transition-transform', collapsed && 'rotate-180')} />
              {!collapsed && <span className="truncate text-[13px]">Collapse</span>}
            </button>
          )}
        </div>

        <SidebarAccount collapsed={collapsed} />
      </div>
    </>
  );
}

// Resolve the icon for a starred path by matching against the DoctypeConfig
// registry. Falls back to the Star icon for paths that aren't ListView pages
// (rare today but possible if a user stars a custom URL later).
function iconForPath(p: string): React.ComponentType<{ className?: string }> {
  for (const d of Object.values(doctypes)) {
    if (`${d.modulePath}/${d.slug}` === p) return d.icon;
  }
  return Star;
}

/**
 * In-rail search trigger — opens the same ⌘K command palette the top chrome
 * used to host. Expanded: a full "Cari…" field with the shortcut chip.
 * Collapsed: a single centred icon button.
 */
function SearchTrigger({ collapsed }: { collapsed?: boolean }) {
  const togglePalette = useUI((s) => s.togglePalette);
  if (collapsed) {
    return (
      <Tooltip side="right" content="Cari  ⌘K">
        <button
          type="button"
          onClick={togglePalette}
          aria-label="Search"
          className="grid place-items-center size-9 rounded-md text-steel hover:bg-surface hover:text-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green/40"
        >
          <Search className="size-4" />
        </button>
      </Tooltip>
    );
  }
  return (
    <button
      type="button"
      onClick={togglePalette}
      className={cn(
        'w-full flex items-center gap-2.5 rounded-lg border border-hairline px-3 h-10 text-left',
        'hover:bg-surface hover:border-stone/40 transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green/40',
      )}
    >
      <Search className="size-[18px] text-stone shrink-0" />
      <span className="flex-1 text-[14px] text-stone">Cari…</span>
      <span className="flex items-center gap-0.5">
        <Kbd>⌘</Kbd><Kbd>K</Kbd>
      </span>
    </button>
  );
}

/**
 * Bottom account card — the signed-in user's home in the rail. Fetches the
 * current user (same `me()` source the top chrome uses) and opens a dropdown
 * with account settings + log out. Collapses to a bare avatar on the 64px rail.
 */
function SidebarAccount({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<{ full_name: string; email: string } | null>(null);
  useEffect(() => {
    me()
      .then((u) => setUser({ full_name: u.full_name || u.email, email: u.email }))
      .catch(() => setUser(null));
  }, []);

  const trigger = (
    <button
      type="button"
      className={cn(
        'flex items-center gap-2.5 w-full rounded-lg border border-hairline p-2 text-left',
        'hover:bg-surface transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green/40',
        collapsed && 'justify-center border-0 p-0',
      )}
    >
      <Avatar name={user?.full_name} size="md" />
      {!collapsed && (
        <>
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block text-[14px] font-medium text-ink truncate">{user?.full_name ?? 'Loading…'}</span>
            <span className="block text-[12px] text-stone truncate">{user?.email}</span>
          </span>
          <MoreVertical className="size-5 text-stone shrink-0" />
        </>
      )}
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="min-w-[220px]">
        <DropdownMenuLabel>
          <div className="text-body-sm font-medium text-ink">{user?.full_name ?? 'Loading…'}</div>
          <div className="text-caption font-normal text-stone">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => void navigate({
            to: '/settings/$section' as never,
            params: { section: 'company' } as never,
          })}
        >
          Account settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => { await logout(); window.location.href = '/login'; }}
          className="text-brand-error data-[highlighted]:bg-brand-error/10 data-[highlighted]:text-brand-error"
        >
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function WorkspaceSwitcher() {
  const brand = useUI((s) => s.brand);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex items-center gap-2 w-full p-1.5 rounded-md hover:bg-surface transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green/40',
        )}
      >
        <div className="flex-1 min-w-0 text-left">
          <div className="text-body-sm font-medium text-ink truncate">{brand.name}</div>
          <div className="text-caption text-stone truncate">{brand.tagline}</div>
        </div>
        <ChevronsUpDown className="size-3.5 text-stone shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start" className="min-w-[220px]">
        <div className="px-2.5 py-1.5 text-caption text-stone">Companies</div>
        <DropdownMenuItem>
          <Building2 className="size-4 text-stone" />
          <span>{brand.name}</span>
          <span className="ml-auto text-caption text-brand-green">● Active</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <Plus className="size-4 text-stone" />
          <span>Create company</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
